import type { Vehicle, VehicleFilters } from '../types/vehicle'

export const WHATSAPP_NUMBER = '5491156449518'

/**
 * Link corto oficial de WhatsApp Business de VCARS (ventas).
 * Se usa en todos los botones de WhatsApp del sitio, salvo el de Administración,
 * que sigue apuntando a su propio número.
 */
export const WHATSAPP_LINK = 'https://wa.me/message/YRVFDIQWAIUNM1'

export function formatPrice(precio: number, moneda: string): string {
  const formatted = new Intl.NumberFormat('es-AR', {
    maximumFractionDigits: 0,
  }).format(precio)
  return moneda === 'USD' ? `USD ${formatted}` : `$ ${formatted}`
}

export function formatKm(km: number): string {
  if (km === 0) return '0 km'
  return `${new Intl.NumberFormat('es-AR').format(km)} km`
}

export function vehicleTitle(v: Vehicle): string {
  return `${v.marca} ${v.modelo} ${v.version}`
}

export function whatsappLink(message: string, phone = WHATSAPP_NUMBER): string {
  // Ventas: link corto de WhatsApp Business (no admite mensaje precargado).
  if (phone === WHATSAPP_NUMBER) return WHATSAPP_LINK
  return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
}

export const GENERAL_WA_MESSAGE = 'Hola VCARS, quiero hablar con un vendedor.'

export function cardWhatsappMessage(v: Vehicle): string {
  return `Hola VCARS, me interesa el ${v.marca} ${v.modelo} ${v.version} ${v.anio} publicado en la web. ¿Sigue disponible?`
}

export function detailWhatsappMessage(v: Vehicle): string {
  return `Hola VCARS, quiero consultar por el ${v.marca} ${v.modelo} ${v.version} ${v.anio}. ¿Podemos coordinar una visita o recibir más información?`
}

function matchesKeyword(v: Vehicle, q: string): boolean {
  const haystack = [
    v.marca,
    v.modelo,
    v.version,
    String(v.anio),
    v.descripcionCorta,
    v.descripcionCompleta,
    v.tipo,
    v.estado,
  ]
    .join(' ')
    .toLowerCase()
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((token) => haystack.includes(token))
}

export function filterVehicles(
  vehicles: Vehicle[],
  f: VehicleFilters,
): Vehicle[] {
  const result = vehicles.filter((v) => {
    if (v.disponible === false) return false
    if (f.q.trim() && !matchesKeyword(v, f.q.trim())) return false
    if (f.marca && v.marca !== f.marca) return false
    if (f.modelo && v.modelo !== f.modelo) return false
    if (f.anioDesde && v.anio < Number(f.anioDesde)) return false
    if (f.anioHasta && v.anio > Number(f.anioHasta)) return false
    if (f.precioMin && v.precio < Number(f.precioMin)) return false
    if (f.precioMax && v.precio > Number(f.precioMax)) return false
    if (f.kmMax && v.kilometraje > Number(f.kmMax)) return false
    if (f.tipo && v.tipo !== f.tipo) return false
    if (f.estado && v.estado !== f.estado) return false
    if (f.combustible && v.combustible !== f.combustible) return false
    if (f.transmision && v.transmision !== f.transmision) return false
    if (f.financiacion && !v.financiacion) return false
    if (f.permuta && !v.permuta) return false
    if (f.oportunidad && !v.oportunidad) return false
    return true
  })

  const sorted = [...result]
  switch (f.sort) {
    case 'precio-asc':
      sorted.sort((a, b) => a.precio - b.precio)
      break
    case 'precio-desc':
      sorted.sort((a, b) => b.precio - a.precio)
      break
    case 'recientes':
      sorted.sort(
        (a, b) => +new Date(b.fechaPublicacion) - +new Date(a.fechaPublicacion),
      )
      break
    default:
      sorted.sort((a, b) => Number(b.destacado) - Number(a.destacado))
  }
  return sorted
}

export function similarVehicles(vehicles: Vehicle[], v: Vehicle): Vehicle[] {
  return vehicles
    .filter((x) => x.id !== v.id && x.disponible)
    .sort((a, b) => {
      const score = (x: Vehicle) =>
        (x.tipo === v.tipo ? 2 : 0) + (x.marca === v.marca ? 1 : 0)
      return score(b) - score(a)
    })
    .slice(0, 3)
}
